/**
 * <kac-frame-preview>
 * Mirrors the theme's variant picker (Size / Frame / Border radios) into a live
 * framed-collage preview, and lets photo swatches select the Frame option by
 * clicking the theme's own (hidden) radio - so price, variant id and the
 * add-to-cart form stay entirely under the theme's control.
 */
class KacFramePreview extends HTMLElement {
  connectedCallback() {
    this.config = JSON.parse(this.querySelector('[data-kac-config]').textContent);
    this.scope = this.closest('.shopify-section') || document;
    this.scope.classList?.add('kac-has-frame-swatches');
    this.frame = this.querySelector('[data-kac-frame]');
    this.collage = this.querySelector('[data-kac-collage]');
    this.caption = this.querySelector('[data-kac-caption]');
    this.frameName = this.querySelector('[data-kac-frame-name]');
    this.swatches = [...this.querySelectorAll('.kac-swatch')];
    this.codes = Object.fromEntries(this.swatches.map((s) => [s.dataset.value, s.dataset.code]));

    this.state = {};
    this.readPicker();
    this.render();

    this.onChange = (event) => {
      const input = event.target;
      if (!(input instanceof HTMLInputElement) || !input.dataset.optionName) return;
      if (!this.scope.contains(input)) return;
      this.state[input.dataset.optionName] = input.value;
      this.render();
    };
    document.addEventListener('change', this.onChange);

    this.swatches.forEach((swatch) =>
      swatch.addEventListener('click', () => this.selectFrame(swatch.dataset.value))
    );
  }

  disconnectedCallback() {
    document.removeEventListener('change', this.onChange);
  }

  readPicker() {
    this.scope.querySelectorAll('input[data-option-name]:checked').forEach((input) => {
      this.state[input.dataset.optionName] = input.value;
    });
  }

  selectFrame(value) {
    const name = this.config.optionNames.frame;
    const input = [...this.scope.querySelectorAll(`input[data-option-name="${name}"]`)].find(
      (i) => i.value === value
    );
    if (input && !input.checked) input.click();
    this.state[name] = value;
    this.render();
  }

  render() {
    const { optionNames, sizes, mouldingUrl } = this.config;
    const frameValue = this.state[optionNames.frame] || '';
    const sizeValue = this.state[optionNames.size] || '';
    const borderValue = this.state[optionNames.border] || '';
    const sizeKey = (sizeValue.match(/A[0-2]/) || ['A2'])[0];
    const size = sizes[sizeKey];
    const code = this.codes[frameValue];
    const bordered = /white/i.test(borderValue);

    this.swatches.forEach((s) => s.setAttribute('aria-pressed', String(s.dataset.value === frameValue)));
    if (this.frameName) this.frameName.textContent = frameValue.replace(' · ', ' — ');

    if (!this.frame) return;
    if (code) {
      const face = code === '1106H' ? 30 : code.startsWith('7008') ? 23 : 20;
      this.frame.style.setProperty('--face', face);
      const strip = (orient) => mouldingUrl.replace(/kac-mh-[^/?]+\.jpg/, `kac-m${orient}-${code}.jpg`);
      this.frame.style.setProperty('--mh', `url("${strip('h')}")`);
      this.frame.style.setProperty('--mv', `url("${strip('v')}")`);
    }
    this.frame.style.setProperty('--sw', size.sw);
    this.frame.dataset.border = String(bordered);

    if (this.collage) {
      this.collage.style.setProperty('--cols', size.cols);
      this.collage.style.setProperty('--rows', size.rows);
      const shown = size.cols * size.rows;
      // Tiles are <svg>, which has no .hidden property - toggle the attribute itself.
      [...this.collage.children].forEach((tile, i) => tile.toggleAttribute('hidden', i >= shown));
    }

    if (this.caption) {
      const sizeName = sizeValue.split(' · ')[0] || sizeKey;
      this.caption.innerHTML =
        `<strong>${sizeName} · ${sizeKey}</strong> ${size.label} · ` +
        `${bordered ? 'white border' : 'no border'}<br>` +
        `Holds roughly ${size.pieces} A3 pieces of artwork`;
    }
  }
}

if (!customElements.get('kac-frame-preview')) customElements.define('kac-frame-preview', KacFramePreview);
